import { Slash, BriefcaseEmpty } from '../../icons';
import MatterCard from './matterCard';
import { useFetchMatters } from '../../../hooks/useFetchMatters';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMatter } from '../../../interface/matter';
import { useInView } from 'react-intersection-observer';
import NoData from '../noData';
import { usePage } from '../../../context/PageContext';
import { useLocation } from 'react-router-dom';
import { MY_OWN_FILTER, ROUTES } from '../../../constants/constants';
import { useTransaction } from '../../../context/TransactionContext';
import { useFetchTransactionRestrictions } from '../../../hooks/useFetchTransactionRestrictions';
import { useHandleTransactionRestrictions } from '../../../hooks/useHandleTransactionRestrictions';
import MatterRestrictionDialogs from './matterRestrictionDialogs';
import AddEditTransactionHeader from './addEditTransactionHeader';
import { useTask } from '../../../context/TaskContext';
import { ITask } from '../../../interface/tasks';
import { Spin } from '../../Ant';
import SearchInputWithFilters from '../SearchInputWithFilters';
import { useTimer } from '../../../context/TimerContext';
import { ITimer } from '../../../interface/timer';
import { useFetchTransactionTypes } from '../../../hooks/useFetchTransactionTypes';
import {
  buildMatterSelection,
  DEFAULT_TRANSACTION_TYPE_FIELDS,
  MATTER_SELECTION_PAGE_SIZE,
  MATTER_SELECTION_QUERY,
} from './matterSelection';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../../hooks/useEscapeToGoBack';
import { useWorkTime } from '../../../context/WorkTimeContext';
import { IWorkTime } from '../../../interface/workTimes';
import { showToast } from '../../utils/toast';
import { useOwnerFilter } from '../../../hooks/useOwnerFilter';
import { useMatterFilters } from '../../../hooks/useMatterFilters';
import { useLanguage } from '../../../context/LanguageContext';
import { useTransactionParameters } from '../../../context/TransactionParameterContext';
import SortSelector from '../../sortPopover/sortSelector';
import { ISortOption, IActiveSortState } from '../../../interface/common';
import SkeletonCardList from '../../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import { useDiscardDialog } from '../../../hooks/useDiscardDialog';
import { isTransactionFormChanged } from './isTransactionFormChanged';
import { isTaskFormChanged } from '../../task/isTaskFormChanged';
import { isCriticalTaskFormChanged } from '../../criticalTask/isCriticalTaskFormChanged';
import { isWorkTimeFormChanged } from '../../worktime/isWorkTimeFormChanged';
import { isTimerFormChanged } from '../../timer/isTimerFormChanged';
import CustomModal from '../../informationPopup/customModal';
import { useListViewPreference } from '../../../context/ListViewPreferenceContext';

const SelectMatter = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { transaction, updateTransaction, initialTransaction } = useTransaction();
  const { parameters } = useTransactionParameters();
  const { getAppLanguageCode } = useLanguage();
  const { task, updateTask, initialTask, clearInitialTask } = useTask();
  const { timer, updateTimer, initialTimer } = useTimer();
  const { workTime, updateWorkTime, initialWorkTime } = useWorkTime();
  const { setTitle } = usePage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { getActiveFavorite } = useListViewPreference();
  const [isFavourite, setIsFavourite] = useState<boolean>(getActiveFavorite('matters'));
  const [searchValue, setSearchValue] = useState<string>('');
  const ownerFilter = useOwnerFilter({ isMyOwnDefault: true });
  const {
    invoiceableTypeFilters,
    matterTypeFilters,
    setSelectedInvoiceableType,
    setSelectedMatterTypes,
    selectedMatterFilters,
    matterFilterParams,
    removeMatterFilter,
    clearMatterFilters,
    hasMoreMatterTypes,
    isFetchingMoreMatterTypes,
    loadMoreMatterTypes,
  } = useMatterFilters();
  const [selectedMatter, setSelectedMatter] = useState<IMatter | undefined>(undefined);
  const previouslySelectedMatterIdRef = useRef<string | undefined>(transaction?.matter?.id);
  const isTaskRoute = location.pathname.includes(ROUTES.TASK_MATTER_SELECTION);
  const isTimerRoute = location?.state?.isFromTimer ?? false;
  const isFromWorkTime = location?.state?.isFromWorkTime ?? false;
  const isFromTimesheet = location?.state?.isFromTimesheet ?? false;
  const temporaryMatterTimesheet = location?.state?.temporaryMatter ?? undefined;
  const autoNavigatedToMatterSelection = location?.state?.autoNavigatedToMatterSelection ?? false;
  const isCriticalTaskRoute = isTaskRoute && !!location?.state?.fromCritical;
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const matterSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'openingDate',
        label: t('matter:openingDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'subject', label: t('matter:subject'), type: 'text', defaultDirection: 'ASC' },
      {
        field: 'matterNumber',
        label: t('matter:matterNumber'),
        type: 'number',
        defaultDirection: 'ASC',
      },
      {
        field: 'principal.name',
        label: t('matter:principal'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'openingDate',
    direction: 'DESC',
  });
  const { data: defaultTransactionType } = useFetchTransactionTypes({
    defaultTransactionTypeId: selectedMatter?.defaultTransactionTypeId,
    fields: DEFAULT_TRANSACTION_TYPE_FIELDS,
  });
  const {
    data: matterList,
    isLoading: isMatterLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchMatters({
    ...MATTER_SELECTION_QUERY,
    extraFilters: { ...MATTER_SELECTION_QUERY.extraFilters, ...matterFilterParams },
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    isFavorite: isFavourite,
    pageSize: MATTER_SELECTION_PAGE_SIZE,
    search: searchValue,
    myOwn: ownerFilter.isMyOwn,
  });
  const { data: matterTransactionRestrictions, isLoading: isLoadingTransactionRestrictions } =
    useFetchTransactionRestrictions({
      matterId: selectedMatter?.id ?? '',
      enabled: selectedMatter?.id !== undefined,
    });
  const onSelect = (matter: IMatter) => {
    if (isTaskRoute) {
      updateTask({
        ...task,
        matter,
        matterId: matter?.id,
      } as ITask);
      setTimeout(() => {
        goBackWithSelectedMatter();
      }, 500);
      return;
    }
    if (isTimerRoute) {
      updateTimer({
        ...timer,
        matter,
        matterId: matter?.id,
        isTransactionTypeManuallyAdded: false,
      } as ITimer);
      setTimeout(() => {
        goBackWithSelectedMatter();
      }, 500);
      return;
    }
    if (isFromWorkTime) {
      updateWorkTime({
        ...workTime,
        matter,
        matterId: matter?.id,
      } as IWorkTime);
      setTimeout(() => {
        goBackWithSelectedMatter();
      }, 500);
      return;
    }
    if (isFromTimesheet) {
      goBackWithSelectedMatter({
        ...matter,
        ...(matter?.defaultTransactionTypeId && {
          transactionType: defaultTransactionType,
          transactionTypeId: defaultTransactionType?.id,
          unitId: defaultTransactionType?.unitId,
          unit: defaultTransactionType?.unit,
        }),
      } as IMatter);
      return;
    }
    updateTransaction(
      buildMatterSelection(transaction, matter, {
        defaultTransactionType,
        transactionTypeInUse: parameters?.transactionTypeInUse,
        appLanguage: getAppLanguageCode(),
      }),
    );
    setTimeout(() => {
      goBackWithSelectedMatter();
    }, 500);
  };

  const {
    serviceRestrictionErrorDialog,
    setServiceRestrictionErrorDialog,
    customerIdentificationErrorDialog,
    setCustomerIdentificationErrorDialog,
  } = useHandleTransactionRestrictions({
    selectedMatter,
    matterTransactionRestrictions,
    onSelect,
  });
  const buildReturnState = (matter?: IMatter) => ({
    matter: matter ?? temporaryMatterTimesheet,
    ...(autoNavigatedToMatterSelection && { autoNavigatedToMatterSelection }),
  });
  const handleBack = () => {
    goBack(buildReturnState());
  };
  const goBackWithSelectedMatter = (matter?: IMatter) => {
    goBackWithSelection(buildReturnState(matter));
  };
  useEscapeToGoBack(() => handleBack());
  const handleClose = () => {
    if (isTaskRoute) {
      const isFormChanged = isCriticalTaskRoute
        ? isCriticalTaskFormChanged(task, initialTask, !!task?.id)
        : isTaskFormChanged(task, initialTask, !!task?.id);
      promptDiscardIfChanged(isFormChanged, () => {
        updateTask(null);
        clearInitialTask();
        goBack(undefined, 2);
      });
      return;
    }
    if (isFromWorkTime) {
      promptDiscardIfChanged(
        isWorkTimeFormChanged(workTime, initialWorkTime, !!workTime?.id),
        () => {
          updateWorkTime(null);
          goBack(undefined, 2);
        },
      );
      return;
    }
    if (isFromTimesheet) {
      // Timesheet holds no draft form data to lose, so closing is just going back — no discard check needed.
      handleBack();
      return;
    }
    if (isTimerRoute) {
      promptDiscardIfChanged(isTimerFormChanged(timer, initialTimer, !!timer?.id), () => {
        updateTimer(null);
        goBack(undefined, 2);
      });
      return;
    }
    promptDiscardIfChanged(
      isTransactionFormChanged(transaction, initialTransaction, !!transaction?.id),
      () => {
        updateTransaction(null);
        goBack(undefined, 2);
      },
    );
  };

  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  const handleTransaction = (matter: IMatter) => {
    if (isFromTimesheet && temporaryMatterTimesheet?.id === matter?.id) {
      showToast('info', t('common:timesheet.matterAlreadyAddedValidation'));
      return;
    }
    previouslySelectedMatterIdRef.current = selectedMatter?.id;
    setSelectedMatter(matter);
  };

  const refreshMatterList = () =>
    refetch().then(({ error: refreshError }) => {
      if (refreshError) showToast('error', refreshError.message);
    });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && matterList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={() => handleBack()}
        handleClose={handleClose}
        showBackWithCross
        title={t('matter:selectMatter')}
        showCross
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };

  return (
    <>
      <CollapsibleListLayout
        testId="matter-selection"
        header={
          <SearchInputWithFilters
            searchTitle={t('matter:search')}
            searchValue={searchValue}
            handleSearch={setSearchValue}
            autoFocus
            tabToResults
            handleRefresh={refreshMatterList}
            filterTitle={t('matter:filter.matterFilters')}
            ownerOptions={ownerFilter.options}
            ownerTitle={ownerFilter.title}
            setSelectedOwner={ownerFilter.select}
            radioTitle={t('matter:filter.invoiceableType')}
            checkboxTitle={t('matter:filter.matterType')}
            radioOptions={invoiceableTypeFilters}
            checkboxOptions={matterTypeFilters}
            selectedFilter={[...ownerFilter.selectedOptions, ...selectedMatterFilters]}
            setSelectedFilter={(val) => setSelectedInvoiceableType(val)}
            setSelectedCheckboxFilter={(val) => setSelectedMatterTypes(val)}
            clearFilter={(val?: string) => {
              if (!val) {
                ownerFilter.reset();
                clearMatterFilters();
                return;
              }
              if (val === MY_OWN_FILTER) {
                ownerFilter.reset();
                return;
              }
              removeMatterFilter(val);
            }}
            hasMoreCheckboxOptions={hasMoreMatterTypes}
            isFetchingMoreCheckboxOptions={isFetchingMoreMatterTypes}
            onLoadMoreCheckboxOptions={loadMoreMatterTypes}
            isFavouriteFilterActive={isFavourite}
            handleFavouriteToggle={setIsFavourite}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={matterSortOptions}
              onApply={(sort) => setActiveSort(sort)}
              activeSort={activeSort}
            />
          </SearchInputWithFilters>
        }
      >
        {matterList &&
          matterList.length > 0 &&
          matterList.map((matter: IMatter) => {
            return (
              <MatterCard
                key={matter?.id}
                matter={matter}
                selectedMatter={temporaryMatterTimesheet}
                onClick={(value) => handleTransaction(value)}
              />
            );
          })}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {(!matterList || (Object.entries(matterList).length === 0 && !isMatterLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('matter:noMatters')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<BriefcaseEmpty />}
            />
          </div>
        )}
        {isMatterLoading ? (
          <SkeletonCardList rows={[1]} withSubcontentOnFirstRow />
        ) : (
          renderLoader()
        )}
      </CollapsibleListLayout>
      <MatterRestrictionDialogs
        customerIdentificationErrorDialog={customerIdentificationErrorDialog}
        setCustomerIdentificationErrorDialog={setCustomerIdentificationErrorDialog}
        serviceRestrictionErrorDialog={serviceRestrictionErrorDialog}
        setServiceRestrictionErrorDialog={setServiceRestrictionErrorDialog}
      />

      {isLoadingTransactionRestrictions && <Spin fullscreen />}
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default SelectMatter;
